import { Moon, Sun } from 'lucide-react'
import { setDisplayPref, useDisplayPrefs } from '../../lib/display-prefs'
import { effectiveDisplay } from '../../lib/display-prefs-core'

/**
 * Pill-style Light / Dark quick switch for the app header.
 *
 * A rounded track with a white knob that slides: LEFT in light mode
 * (green sun) → RIGHT in dark mode (green moon). The track darkens in
 * dark mode. Colours are driven off the theme actually on screen (not
 * `dark:` variants) so the switch always mirrors the current state.
 *
 * It writes the SAME account preference as My Preferences (one source of
 * truth, saved to the server). A click picks an explicit Light or Dark;
 * "Computer" (follow the computer's setting) is chosen on My Preferences,
 * and is never lost — it is one of that page's three options.
 */
export function ThemeToggle({ className = '' }) {
  const { effective, system } = useDisplayPrefs()
  const isDark = effectiveDisplay(effective, system).theme === 'dark'
  const label = isDark ? 'Switch to light mode' : 'Switch to dark mode'

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={label}
      title={label}
      onClick={() => void setDisplayPref('theme', isDark ? 'light' : 'dark')}
      className={
        'relative inline-flex h-[28px] w-[52px] shrink-0 items-center rounded-full ' +
        'cursor-pointer transition-colors outline-none ' +
        'focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)] ' +
        (isDark ? 'bg-slate-700' : 'bg-slate-300') +
        ' ' +
        className
      }
    >
      <span
        className={
          'inline-flex h-[24px] w-[24px] items-center justify-center rounded-full bg-white ' +
          'shadow-md transition-transform duration-200 ease-out ' +
          (isDark ? 'translate-x-[26px]' : 'translate-x-[2px]')
        }
      >
        {isDark ? (
          <Moon size={13} className="text-green-500" fill="currentColor" />
        ) : (
          <Sun size={13} className="text-green-500" />
        )}
      </span>
    </button>
  )
}
